import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

const DRAG_THRESHOLD_PX = 5;
/** Glide after release: speed (px/ms) decays with this time constant, like a native touch fling. */
const GLIDE_TIME_CONSTANT_MS = 325;
const GLIDE_MIN_SPEED = 0.02;
const GLIDE_MAX_SPEED = 3;
/** A pointer that rested this long before release was placed, not flung. */
const GLIDE_STALE_MS = 80;

type DragState = {
  active: boolean;
  moved: boolean;
  startX: number;
  startScrollLeft: number;
  pointerId: number;
  lastX: number;
  lastTime: number;
  /** Pointer speed in px/ms, smoothed over the last few moves. */
  velocity: number;
};

const idleDragState = (): DragState => ({
  active: false,
  moved: false,
  startX: 0,
  startScrollLeft: 0,
  pointerId: -1,
  lastX: 0,
  lastTime: 0,
  velocity: 0,
});

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Mouse drag → horizontal scroll for overflow-x rows, with a short glide after release.
 * Touch and pen are left to the browser: it already pans these rows with its own momentum, and
 * moving the row from here as well made the two fight each other.
 * Suppresses accidental clicks on children after a drag gesture.
 */
export function useHorizontalDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const scrollRef = useRef<T | null>(null);
  const dragRef = useRef<DragState>(idleDragState());
  const glideFrameRef = useRef<number | null>(null);
  /** The row's own inline scroll-behavior, put back once the drag and its glide are over. */
  const savedScrollBehaviorRef = useRef<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const setRef = useCallback((node: T | null) => {
    scrollRef.current = node;
  }, []);

  /** Follow the pointer exactly: CSS smooth scrolling would animate every step and lag behind. */
  const takeOverScrolling = useCallback((el: T) => {
    if (savedScrollBehaviorRef.current === null) {
      savedScrollBehaviorRef.current = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
    }
  }, []);

  const releaseScrolling = useCallback(() => {
    const el = scrollRef.current;
    if (el && savedScrollBehaviorRef.current !== null) {
      el.style.scrollBehavior = savedScrollBehaviorRef.current;
    }
    savedScrollBehaviorRef.current = null;
  }, []);

  const stopGlide = useCallback(() => {
    if (glideFrameRef.current !== null) {
      cancelAnimationFrame(glideFrameRef.current);
      glideFrameRef.current = null;
    }
  }, []);

  const startGlide = useCallback(
    (el: T, initialVelocity: number) => {
      let velocity = Math.max(-GLIDE_MAX_SPEED, Math.min(GLIDE_MAX_SPEED, initialVelocity));
      let position = el.scrollLeft;
      let lastTime = now();

      const step = () => {
        const time = now();
        const elapsed = time - lastTime;
        lastTime = time;

        position -= velocity * elapsed;
        el.scrollLeft = position;
        velocity *= Math.exp(-elapsed / GLIDE_TIME_CONSTANT_MS);

        // Stopped by the end of the row, or too slow to see.
        const hitEdge = Math.abs(el.scrollLeft - position) > 1;
        if (hitEdge || Math.abs(velocity) < GLIDE_MIN_SPEED) {
          glideFrameRef.current = null;
          releaseScrolling();
          return;
        }
        glideFrameRef.current = requestAnimationFrame(step);
      };

      glideFrameRef.current = requestAnimationFrame(step);
    },
    [releaseScrolling]
  );

  useEffect(
    () => () => {
      stopGlide();
      releaseScrolling();
    },
    [releaseScrolling, stopGlide]
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<T>) => {
      if (event.button !== 0) return;
      if (event.pointerType && event.pointerType !== 'mouse') return;

      const el = scrollRef.current;
      if (!el || el.scrollWidth <= el.clientWidth + 1) return;

      // Pressing on a row that is still gliding catches it.
      stopGlide();

      dragRef.current = {
        active: true,
        moved: false,
        startX: event.clientX,
        startScrollLeft: el.scrollLeft,
        pointerId: event.pointerId,
        lastX: event.clientX,
        lastTime: now(),
        velocity: 0,
      };
    },
    [stopGlide]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<T>) => {
      const drag = dragRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;

      const el = scrollRef.current;
      if (!el) return;

      if (!drag.moved) {
        if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX) return;
        drag.moved = true;
        // Start from where the pointer is now, so the row does not jump by the threshold.
        drag.startX = event.clientX;
        drag.startScrollLeft = el.scrollLeft;
        drag.lastX = event.clientX;
        drag.lastTime = now();
        takeOverScrolling(el);
        setIsDragging(true);
        el.setPointerCapture(event.pointerId);
        event.preventDefault();
        return;
      }

      const time = now();
      const elapsed = time - drag.lastTime;
      if (elapsed > 0) {
        const instant = (event.clientX - drag.lastX) / elapsed;
        drag.velocity = 0.8 * instant + 0.2 * drag.velocity;
        drag.lastX = event.clientX;
        drag.lastTime = time;
      }

      el.scrollLeft = drag.startScrollLeft - (event.clientX - drag.startX);
      event.preventDefault();
    },
    [takeOverScrolling]
  );

  const finishDrag = useCallback(
    (event: ReactPointerEvent<T>) => {
      const drag = dragRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;

      const el = scrollRef.current;
      if (el?.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }

      if (drag.moved && el) {
        const suppressClick = (clickEvent: Event) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        el.addEventListener('click', suppressClick, { capture: true, once: true });
        // The click that ends this gesture is dispatched before any timer runs. If the browser
        // sends none (pointer released elsewhere), drop the guard so it cannot eat the next real tap.
        window.setTimeout(() => el.removeEventListener('click', suppressClick, { capture: true }), 0);

        const flung = now() - drag.lastTime <= GLIDE_STALE_MS && Math.abs(drag.velocity) >= GLIDE_MIN_SPEED;
        const animationsOff = typeof document !== 'undefined' && document.body.classList.contains('animations-off');
        if (flung && !animationsOff && event.type !== 'pointercancel') {
          startGlide(el, drag.velocity);
        } else {
          releaseScrolling();
        }
      }

      dragRef.current = idleDragState();
      setIsDragging(false);
    },
    [releaseScrolling, startGlide]
  );

  return {
    scrollRef: setRef,
    isDragging,
    dragScrollProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
    },
  };
}
